/**
 * accountRootKey.ts — the per-ACCOUNT secret that participates in v2
 * Snapshot Master Key derivation.
 *
 * Key model
 * ---------
 * One 32-byte `account_root_key` exists per account. It is the canonical
 * secret that all of that account's devices share. The server holds it
 * ONLY in wrapped form (encrypted under a stretched recovery passphrase),
 * so server state alone cannot derive any v2 SMK.
 *
 * Lifecycle on a given device
 * ---------------------------
 *  - First-ever device for the account: caller generates the key
 *    (`generateAccountRootKey()`), wraps it with the recovery flow
 *    (see `recoveryMnemonic.ts`), uploads the wrap to the server, and
 *    persists the bare key here via `installAccountRootKey()`.
 *  - Subsequent device for the same account: either iCloud Keychain
 *    delivers the key automatically (transparent path; see Storage below),
 *    OR the caller fetches the server wrap, prompts the user for the
 *    recovery mnemonic, unwraps, and installs locally.
 *  - Sign-out / explicit reset: `wipeAccountRootKey()` removes the local
 *    copy AND the synced copy (so a future re-install doesn't silently
 *    pick up a stale key from iCloud).
 *
 * Storage (TWO layers)
 * --------------------
 *  1. Local SecureStore slot (`snap_account_root_${accountId}`,
 *     WHEN_PASSCODE_SET_THIS_DEVICE_ONLY): the canonical fast read path
 *     for everyday snapshot reads/writes on this device.
 *
 *  2. Synced iCloud-Keychain slot (`snap_account_root_synced_${accountId}`,
 *     via `syncedKeychain.ts`, kSecAttrSynchronizable + AfterFirstUnlock):
 *     the transparent cross-device delivery channel. When the user installs
 *     the app on a new iPhone signed into the same Apple ID with iCloud
 *     Keychain enabled, this slot reappears automatically and we promote
 *     it back into the local SecureStore slot on first read.
 *
 * Reads check (1) first; on miss they check (2), and if (2) hits we mirror
 * back into (1) so subsequent reads stay fast and don't depend on the
 * native bridge being available. Writes update both. Wipes clear both.
 *
 * If the user has iCloud Keychain DISABLED, layer (2) is a local-only
 * keychain entry — sync silently never happens, and the user falls back to
 * the mnemonic recovery flow on a new device. We can't detect "iCloud
 * Keychain off" from a third-party app; the recovery flow remains as the
 * universal fallback.
 *
 * On Android, the native bridge reports unavailable, so we behave as if
 * layer (2) doesn't exist. Block Store integration is the cross-platform
 * follow-up; for now Android relies on the mnemonic flow.
 */

import * as SecureStore from 'expo-secure-store';
import { canHoldSnapshotKeys, SnapshotKeyPolicyError } from './snapshotKeyPolicy';
import * as Crypto from 'expo-crypto';
import { bytesToHex, hexToBytes } from '@noble/ciphers/utils.js';
import {
  isSyncedKeychainAvailable,
  setSyncedKeychainItem,
  getSyncedKeychainItem,
  deleteSyncedKeychainItem,
} from './syncedKeychain';

const KEYCHAIN_ACCESSIBLE = SecureStore.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY;

const localSlot = (accountId: string) => `snap_account_root_${accountId}`;
const syncedSlot = (accountId: string) => `snap_account_root_synced_${accountId}`;

// 64 lowercase/uppercase hex chars = 32 bytes. Anything else in either slot is
// either corruption or someone else's data; we refuse to use it rather than
// throw out of `hexToBytes` deep inside snapshot crypto.
const ROOT_KEY_HEX_RE = /^[0-9a-f]{64}$/i;

function isValidRootKeyHex(value: string | null | undefined): value is string {
  return typeof value === 'string' && ROOT_KEY_HEX_RE.test(value);
}

/** Generate a fresh 32-byte account root key. Caller must wrap + upload. */
export async function generateAccountRootKey(): Promise<Uint8Array> {
  const buf = await Crypto.getRandomBytesAsync(32);
  return new Uint8Array(buf);
}

async function writeLocal(accountId: string, hex: string): Promise<void> {
  const opts = { keychainAccessible: KEYCHAIN_ACCESSIBLE };
  try {
    await SecureStore.setItemAsync(localSlot(accountId), hex, opts);
  } catch {
    // Android Keystore disallows in-place updates of biometric-protected
    // items; delete-and-recreate clears that path.
    await SecureStore.deleteItemAsync(localSlot(accountId)).catch(() => {});
    await SecureStore.setItemAsync(localSlot(accountId), hex, opts);
  }
}

/**
 * Persist the canonical account root key for `accountId`. Writes to BOTH
 * the local SecureStore slot (fast read path) AND the iCloud-synchronizable
 * keychain slot (cross-device delivery). Either path that produces the key
 * — fresh generation on a first device, successful unwrap on a recovery
 * device, or auto-promotion from the synced slot on a brand-new install —
 * ends here.
 *
 * Writing to the synced slot may silently fail on Android or on iOS builds
 * without the SyncedKeychainModule; that's expected and the local write is
 * still durable. The mnemonic recovery flow remains as the universal
 * fallback for cross-device delivery in those cases.
 */
export async function installAccountRootKey(
  accountId: string,
  rootKey: Uint8Array,
): Promise<void> {
  if (!accountId) throw new Error('installAccountRootKey requires an accountId');
  if (rootKey.length !== 32) throw new Error('Account root key must be exactly 32 bytes.');
  // Deepest chokepoint on purpose: every setup/restore path funnels here,
  // so a future caller cannot re-introduce browser-localStorage keys by
  // simply not knowing the rule.
  if (!canHoldSnapshotKeys()) throw new SnapshotKeyPolicyError();
  const hex = bytesToHex(rootKey);

  // Local write must succeed for this device to function. Synced write
  // failures are a degraded-mode signal but not a hard error.
  await writeLocal(accountId, hex);
  if (isSyncedKeychainAvailable()) {
    await setSyncedKeychainItem(syncedSlot(accountId), hex).catch(() => false);
  }
}

/**
 * Return the canonical account root key on this device. Tries the local
 * SecureStore slot first; on miss, falls back to the iCloud-synchronizable
 * slot — which may have just been delivered by Apple after a fresh install
 * on a new device. A hit on the synced slot is mirrored back into the
 * local slot so subsequent reads don't pay the bridge round-trip.
 *
 * Returns null only if neither layer has the key, in which case the caller
 * must surface the mnemonic recovery prompt.
 */
export async function getAccountRootKey(accountId: string): Promise<Uint8Array | null> {
  if (!accountId) return null;

  const localHex = await SecureStore.getItemAsync(localSlot(accountId)).catch(() => null);
  if (isValidRootKeyHex(localHex)) return hexToBytes(localHex);
  if (localHex) {
    // Local slot held something other than a 32-byte hex string. Treat as
    // corruption: drop it so we don't keep trying to promote/read it, and
    // fall through to the synced layer. The user may still recover via
    // mnemonic if both layers come up empty.
    await SecureStore.deleteItemAsync(localSlot(accountId)).catch(() => {});
  }

  if (!isSyncedKeychainAvailable()) return null;
  const syncedHex = await getSyncedKeychainItem(syncedSlot(accountId));
  if (!isValidRootKeyHex(syncedHex)) {
    if (syncedHex) {
      // Don't promote junk into local. Drop the synced value too — keeping
      // it would force every read to pay the bridge round-trip and re-fail.
      await deleteSyncedKeychainItem(syncedSlot(accountId)).catch(() => false);
    }
    return null;
  }

  // Promote the synced value into the local slot so future reads stay
  // fast. Failure to promote is non-fatal — we still return the key.
  await writeLocal(accountId, syncedHex).catch(() => {});
  return hexToBytes(syncedHex);
}

/** True iff this device can produce the account root key (local or synced). */
export async function hasAccountRootKey(accountId: string): Promise<boolean> {
  return (await getAccountRootKey(accountId)) !== null;
}

/**
 * Remove BOTH the local and synced copies of the account root key. The
 * server-side wrap is untouched — recovery via mnemonic still works.
 *
 * We clear the synced copy too because leaving it in iCloud Keychain would
 * cause `getAccountRootKey()` to silently re-promote it on the next app
 * launch, which is rarely what a user wants when they explicitly reset.
 * Re-installing later (via setup or recovery) will write fresh values to
 * both layers.
 */
export async function wipeAccountRootKey(accountId: string): Promise<void> {
  if (!accountId) return;
  await Promise.all([
    SecureStore.deleteItemAsync(localSlot(accountId)).catch(() => {}),
    isSyncedKeychainAvailable()
      ? deleteSyncedKeychainItem(syncedSlot(accountId)).catch(() => false)
      : Promise.resolve(false),
  ]);
}
